import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Award, Star, Trophy, Target, BookOpen, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface BadgeData {
  id: string;
  name: string;
  description: string;
  image_url?: string;
  required_course_count?: number;
  required_pd_hours?: number;
  required_course_ids?: string[];
  is_active: boolean;
}

interface UserBadge {
  id: string;
  badge: BadgeData;
  earned_at: string;
  course_id?: string;
}

interface BadgeProgress {
  badge: BadgeData;
  progress: number;
  total: number;
  earned: boolean;
}

export function BadgeSystem() {
  const { user } = useAuth();
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [badgeProgress, setBadgeProgress] = useState<BadgeProgress[]>([]);
  const [selectedBadge, setSelectedBadge] = useState<BadgeData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadUserBadges();
      loadBadgeProgress();
    }
  }, [user]);

  const loadUserBadges = async () => {
    try {
      const { data, error } = await supabase
        .from('issued_certificates')
        .select(`
          *,
          badge:badges(*)
        `)
        .eq('user_id', user!.id)
        .not('badge_id', 'is', null);

      if (error) throw error;
      setUserBadges(data.map(cert => ({
        id: cert.id,
        badge: cert.badge,
        earned_at: cert.issue_date,
        course_id: cert.course_id
      })));
    } catch (error) {
      console.error('Error loading user badges:', error);
    }
  };

  const loadBadgeProgress = async () => {
    try {
      // Get all active badges
      const { data: badges, error: badgesError } = await supabase
        .from('badges')
        .select('*')
        .eq('is_active', true);

      if (badgesError) throw badgesError;

      // Get user's completed courses and PD hours
      const { data: completedCourses, error: coursesError } = await supabase
        .from('enrollments')
        .select('course_id, courses(duration_hours)')
        .eq('participant_id', user!.id)
        .eq('status', 'completed');

      if (coursesError) throw coursesError;

      const totalPDHours = completedCourses.reduce((sum, enrollment) => 
        sum + (enrollment.courses?.duration_hours || 0), 0);
      const completedCourseIds = completedCourses.map(e => e.course_id);

      // Calculate progress for each badge
      const progress = badges.map(badge => {
        let current = 0;
        let total = 0;

        if (badge.required_pd_hours) {
          current = Math.min(totalPDHours, badge.required_pd_hours);
          total = badge.required_pd_hours;
        } else if (badge.required_course_count) {
          current = completedCourseIds.length;
          total = badge.required_course_count;
        } else if (badge.required_course_ids) {
          current = badge.required_course_ids.filter(id => 
            completedCourseIds.includes(id)).length;
          total = badge.required_course_ids.length;
        }

        const earned = userBadges.some(ub => ub.badge.id === badge.id);
        
        return {
          badge,
          progress: current,
          total,
          earned
        };
      });

      setBadgeProgress(progress);
    } catch (error) {
      console.error('Error loading badge progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBadgeIcon = (badgeType: string) => {
    switch (badgeType) {
      case 'course_completion': return <BookOpen className="h-6 w-6" />;
      case 'pd_hours': return <Clock className="h-6 w-6" />;
      case 'achievement': return <Trophy className="h-6 w-6" />;
      default: return <Award className="h-6 w-6" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-48 bg-muted animate-pulse rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Earned Badges */}
      {userBadges.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Trophy className="h-6 w-6 text-warning" />
            Your Achievements
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {userBadges.map((userBadge) => (
              <Card 
                key={userBadge.id} 
                className="cursor-pointer hover:shadow-lg transition-all duration-300 border-warning/20 bg-gradient-to-br from-warning/5 to-warning/10"
                onClick={() => setSelectedBadge(userBadge.badge)}
              >
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto w-16 h-16 bg-gradient-to-br from-warning to-warning/80 rounded-full flex items-center justify-center mb-2 shadow-glow">
                    {getBadgeIcon('achievement')}
                  </div>
                  <CardTitle className="text-lg">{userBadge.badge.name}</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    {userBadge.badge.description}
                  </p>
                  <Badge variant="default" className="bg-warning text-warning-foreground">
                    Earned {new Date(userBadge.earned_at).toLocaleDateString()}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Badge Progress */}
      <div>
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <Target className="h-6 w-6 text-primary" />
          Badge Progress
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {badgeProgress.map((badgeProgress) => {
            const progressPercentage = badgeProgress.total > 0 
              ? (badgeProgress.progress / badgeProgress.total) * 100 
              : 0;

            return (
              <Card 
                key={badgeProgress.badge.id}
                className={`cursor-pointer hover:shadow-lg transition-all duration-300 ${
                  badgeProgress.earned 
                    ? 'border-success/20 bg-gradient-to-br from-success/5 to-success/10' 
                    : 'hover:border-primary/40'
                }`}
                onClick={() => setSelectedBadge(badgeProgress.badge)}
              >
                <CardHeader className="text-center pb-2">
                  <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-2 ${
                    badgeProgress.earned
                      ? 'bg-gradient-to-br from-success to-success/80 shadow-glow'
                      : 'bg-gradient-to-br from-muted to-muted/80'
                  }`}>
                    {getBadgeIcon('progress')}
                  </div>
                  <CardTitle className="text-lg">{badgeProgress.badge.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3 text-center">
                    {badgeProgress.badge.description}
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span>{badgeProgress.progress}/{badgeProgress.total}</span>
                    </div>
                    <Progress value={progressPercentage} className="h-2" />
                    {badgeProgress.earned && (
                      <Badge variant="default" className="w-full justify-center bg-success text-success-foreground">
                        ✓ Earned!
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Badge Detail Dialog */}
      <Dialog open={!!selectedBadge} onOpenChange={() => setSelectedBadge(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              {selectedBadge?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="text-center space-y-4">
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-primary to-primary/80 rounded-full flex items-center justify-center shadow-glow">
              {selectedBadge && getBadgeIcon('detail')}
            </div>
            <div>
              <p className="text-muted-foreground mb-4">
                {selectedBadge?.description}
              </p>
              <div className="space-y-2 text-sm">
                {selectedBadge?.required_pd_hours && (
                  <div className="flex justify-between">
                    <span>Required PD Hours:</span>
                    <span className="font-medium">{selectedBadge.required_pd_hours}</span>
                  </div>
                )}
                {selectedBadge?.required_course_count && (
                  <div className="flex justify-between">
                    <span>Required Courses:</span>
                    <span className="font-medium">{selectedBadge.required_course_count}</span>
                  </div>
                )}
                {selectedBadge?.required_course_ids && (
                  <div className="flex justify-between">
                    <span>Specific Courses:</span>
                    <span className="font-medium">{selectedBadge.required_course_ids.length}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}